import {
  HomeIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  CurrencyPoundIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  Bars3Icon,
  NewspaperIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import hdlccLogo from '../../assets/HDLCC.avif';
import { useAuth } from '../../contexts/AuthContext';

function AdminLayout({ children }) {
  const location = useLocation();
  const { logout } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  const menuItems = [
    {
      name: 'Dashboard',
      icon: HomeIcon,
      path: '/admin',
    },
    {
      name: 'Members',
      icon: UsersIcon,
      path: '/admin/members',
    },
    {
      name: 'Requests',
      icon: UserGroupIcon,
      path: '/admin/requests',
    },
    {
      name: 'Payments',
      icon: CurrencyPoundIcon,
      path: '/admin/payments',
    },
    {
      name: 'Reports',
      icon: ChartBarIcon,
      path: '/admin/reports',
    },
    {
      name: 'Magazine',
      icon: NewspaperIcon,
      path: '/admin/magazine',
    },
    {
      name: 'Email Templates',
      icon: EnvelopeIcon,
      path: '/admin/email-templates',
    },
    {
      name: 'Communications',
      icon: EnvelopeIcon,
      path: '/admin/communications',
    },
    {
      name: 'Audit Log',
      icon: ClipboardDocumentListIcon,
      path: '/admin/audit-log',
    },
    {
      name: 'Settings',
      icon: CogIcon,
      path: '/admin/settings',
    },
  ];

  const isActivePath = path => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-hdlcc-bgLight flex">
      {/* Expandable Side Navigation */}
      <div
        className={`bg-hdlcc-adminPrimaryDark flex flex-col fixed h-screen transition-all duration-300 ${
          isExpanded ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="py-8 flex items-center justify-center px-4">
          <Link to="/admin" className="flex items-center justify-center">
            <img
              src={hdlccLogo}
              alt="HDLCC"
              className={`object-contain transition-all duration-300 ${isExpanded ? 'h-12 w-12' : 'h-10 w-10'}`}
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 flex flex-col gap-2 w-full ${isExpanded ? 'px-4' : 'items-center'}`}
        >
          {menuItems.map(item => (
            <Link
              key={item.name}
              to={item.path}
              className={`flex items-center gap-3 p-3 rounded-md transition-all ${
                isActivePath(item.path)
                  ? 'bg-white/10 text-hdlcc-accentRed opacity-100'
                  : 'text-hdlcc-textLight opacity-60 hover:opacity-100 hover:bg-white/10 hover:text-hdlcc-accentRed'
              } ${!isExpanded && 'justify-center'}`}
              title={!isExpanded ? item.name : ''}
            >
              <item.icon className="w-6 h-6 flex-shrink-0" />
              {isExpanded && <span className="whitespace-nowrap">{item.name}</span>}
            </Link>
          ))}
        </nav>

        {/* User Profile / Sign Out */}
        <div className={`py-8 ${isExpanded ? 'px-4' : 'flex justify-center'}`}>
          <button
            onClick={logout}
            className={`flex items-center gap-3 p-3 rounded-md transition-all text-hdlcc-textLight opacity-60 hover:opacity-100 hover:bg-white/10 hover:text-hdlcc-accentRed ${!isExpanded && 'justify-center'}`}
            title={!isExpanded ? 'Sign Out' : ''}
          >
            <ArrowRightOnRectangleIcon className="w-6 h-6 flex-shrink-0" />
            {isExpanded && <span>Sign Out</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${isExpanded ? 'ml-64' : 'ml-20'}`}>
        {/* Top Header with Toggle Button */}
        <div className="bg-hdlcc-bgWhite border-b border-gray-200 px-6 py-4 flex items-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-md text-hdlcc-textDark hover:bg-gray-100 transition-colors"
            title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

export default AdminLayout;
